import React, { useEffect, useState } from "react";
import { listReservations } from "../lib/api";
import { Lock, RefreshCw, LogOut, Calendar, Phone, Users, StickyNote } from "lucide-react";

const TOKEN_KEY = "provalo_admin_token";

export const AdminReservations = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async (t) => {
    setLoading(true); setErr("");
    try {
      const data = await listReservations(t);
      setRows(data);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
    } catch (e) {
      if (e?.response?.status === 401) {
        setErr("Token non valido.");
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        setErr("Errore di rete. Riprova.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    setToken(""); setAuthed(false); setRows([]);
    localStorage.removeItem(TOKEN_KEY);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };
  const fmtCreated = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[var(--pn-black)] text-[var(--pn-cream)] flex items-center justify-center p-6" data-testid="admin-login-screen">
        <form
          onSubmit={(e) => { e.preventDefault(); if (token.trim()) load(token.trim()); }}
          className="w-full max-w-[420px] bg-[#15110C]/80 backdrop-blur border border-[var(--pn-amber)]/15 rounded-sm p-8 space-y-6"
        >
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-[var(--pn-amber)]" />
            <h1 className="font-display text-[24px] tracking-tight text-[var(--pn-milk)]">Admin · Prenotazioni</h1>
          </div>
          <p className="text-[14px] text-[var(--pn-cream)]/60 leading-relaxed">
            Inserisci il token amministratore per visualizzare le prenotazioni interne.
          </p>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            autoFocus
            className="w-full bg-[var(--pn-black)] border border-[var(--pn-amber)]/20 rounded-sm px-4 py-3 text-[15px] text-[var(--pn-milk)] outline-none focus:border-[var(--pn-amber)]/60 transition"
            data-testid="admin-token-input"
          />
          {err && <p className="text-[13px] text-[#E0744A]" data-testid="admin-error">{err}</p>}
          <button
            type="submit"
            disabled={loading || !token.trim()}
            className="cta-primary w-full justify-center !text-[15px] !py-[16px] disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? "Verifica…" : "Entra"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--pn-black)] text-[var(--pn-cream)] p-6 md:p-10" data-testid="admin-dashboard">
      <header className="max-w-[1280px] mx-auto flex items-center justify-between mb-8 md:mb-12">
        <div>
          <p className="eyebrow">— Provalo · Gourmet-Naïf</p>
          <h1 className="font-display text-[28px] md:text-[36px] mt-2 text-[var(--pn-milk)]">Prenotazioni interne</h1>
          <p className="text-[13px] text-[var(--pn-cream)]/55 mt-1">
            {rows.length} {rows.length === 1 ? "prenotazione" : "prenotazioni"} totali · ordinate per data di inserimento
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(token)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--pn-amber)]/30 text-[13px] hover:border-[var(--pn-amber)]/60 transition disabled:opacity-50"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span>Aggiorna</span>
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--pn-cream)]/15 text-[13px] text-[var(--pn-cream)]/70 hover:border-[var(--pn-cream)]/40 transition"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} />
            <span>Esci</span>
          </button>
        </div>
      </header>

      {err && <p className="max-w-[1280px] mx-auto mb-4 text-[13px] text-[#E0744A]">{err}</p>}

      <div className="max-w-[1280px] mx-auto">
        {rows.length === 0 ? (
          <div className="text-center py-24 text-[var(--pn-cream)]/55 border border-dashed border-[var(--pn-amber)]/15 rounded-sm" data-testid="admin-empty-state">
            <p className="font-display italic text-[20px] text-[var(--pn-amber)]/70">Nessuna prenotazione ancora.</p>
            <p className="text-[13px] mt-2">Le prenotazioni dal modulo interno compaiono qui.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto border border-[var(--pn-amber)]/12 rounded-sm">
              <table className="w-full text-[14px]" data-testid="admin-reservations-table">
                <thead className="bg-[#15110C] text-[var(--pn-cream)]/65 text-[11px] uppercase tracking-[0.16em]">
                  <tr>
                    <th className="text-left px-5 py-4 font-medium">Nome</th>
                    <th className="text-left px-5 py-4 font-medium">Telefono</th>
                    <th className="text-left px-5 py-4 font-medium">Pax</th>
                    <th className="text-left px-5 py-4 font-medium">Giovedì</th>
                    <th className="text-left px-5 py-4 font-medium">Note</th>
                    <th className="text-left px-5 py-4 font-medium">Inserita</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--pn-amber)]/8 hover:bg-[#15110C]/40 transition" data-testid={`admin-row-${r.id}`}>
                      <td className="px-5 py-4 text-[var(--pn-milk)] font-medium">{r.nome}</td>
                      <td className="px-5 py-4">
                        <a href={`tel:${r.telefono}`} className="text-[var(--pn-amber)] hover:underline">{r.telefono}</a>
                      </td>
                      <td className="px-5 py-4">{r.persone}</td>
                      <td className="px-5 py-4">{fmtDate(r.data_giovedi)}</td>
                      <td className="px-5 py-4 text-[var(--pn-cream)]/75 max-w-[280px]">
                        <span className="line-clamp-2">{r.note || "—"}</span>
                      </td>
                      <td className="px-5 py-4 text-[var(--pn-cream)]/55 text-[12px]">{fmtCreated(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {rows.map((r) => (
                <div key={r.id} className="border border-[var(--pn-amber)]/12 rounded-sm p-5 bg-[#15110C]/50" data-testid={`admin-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-[18px] text-[var(--pn-milk)]">{r.nome}</p>
                      <a href={`tel:${r.telefono}`} className="text-[13px] text-[var(--pn-amber)] mt-0.5 inline-flex items-center gap-1.5">
                        <Phone size={13} /> {r.telefono}
                      </a>
                    </div>
                    <span className="text-[11px] text-[var(--pn-cream)]/50 whitespace-nowrap">{fmtCreated(r.created_at)}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
                    <div className="flex items-center gap-2 text-[var(--pn-cream)]/80">
                      <Calendar size={13} className="text-[var(--pn-amber)]/70" /> {fmtDate(r.data_giovedi)}
                    </div>
                    <div className="flex items-center gap-2 text-[var(--pn-cream)]/80">
                      <Users size={13} className="text-[var(--pn-amber)]/70" /> {r.persone} {r.persone === 1 ? "persona" : "persone"}
                    </div>
                  </div>
                  {r.note && (
                    <div className="mt-3 pt-3 border-t border-[var(--pn-amber)]/8 flex gap-2 text-[13px] text-[var(--pn-cream)]/70">
                      <StickyNote size={13} className="text-[var(--pn-amber)]/70 flex-shrink-0 mt-0.5" />
                      <p>{r.note}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
